import { create } from "zustand";
import { type AnySession, tenantSlugOf } from "../schemas/auth.schemas";

/**
 * idle          nothing read from storage yet
 * restoring     this browser may hold a session cookie; a session is being fetched
 * authenticated a live session is in memory
 * anonymous     no session and nothing to restore from
 */
export type AuthStatus = "idle" | "restoring" | "authenticated" | "anonymous";

export const AUTH_STORAGE_KEYS = {
  /** "1" while this browser may hold a refresh cookie. A hint, never a credential (ADR-0005). */
  hasSession: "simpro.hasSession",
  tenantSlug: "simpro.tenantSlug",
} as const;

/** Where the refresh token lived before ADR-0005. Removed on sight so no copy outlives the change. */
const LEGACY_REFRESH_TOKEN_KEY = "simpro.refreshToken";

export interface AuthState {
  status: AuthStatus;
  /** Only the access token lives in memory; it is never written to storage. */
  session: AnySession | null;
  /**
   * Whether this browser may hold a refresh cookie. The cookie itself is HttpOnly, so the app cannot
   * look; the flag only saves an anonymous visitor a refresh call that could only fail.
   */
  hasSession: boolean;
  tenantSlug: string | null;
  /**
   * True once someone chose to sign out, until the next sign-in. The next person at this browser
   * may be someone else, so login starts them at home rather than on the page the last one left.
   * False when a session merely ran out: then it is the same person coming back.
   */
  signedOut: boolean;
  setSession: (session: AnySession) => void;
  setTenantSlug: (slug: string | null) => void;
  /** Forget everything, in memory and in storage. `signedOut` when a person chose to sign out. */
  clear: (options?: { signedOut?: boolean }) => void;
  /** Read the persisted hint and tenant slug. Idempotent. */
  rehydrate: () => void;
}

function readStorage(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage.removeItem(key);
    else globalThis.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode, SSR); the session still works for this tab.
  }
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  status: "idle",
  session: null,
  hasSession: false,
  tenantSlug: null,
  signedOut: false,

  setSession: (session) => {
    writeStorage(AUTH_STORAGE_KEYS.hasSession, "1");
    // A customer session names no slug: the booking app knows its salon from its own host,
    // and the server reads the tenant from the header rather than from the token. This tab's own
    // slug comes before storage: storage is shared by every tab on the origin, so another tab opened
    // on a different salon (or on none) must not change which salon this tab's next request names.
    const fromSession = tenantSlugOf(session);
    const tenantSlug = fromSession ?? get().tenantSlug ?? readStorage(AUTH_STORAGE_KEYS.tenantSlug);
    if (fromSession) writeStorage(AUTH_STORAGE_KEYS.tenantSlug, fromSession);
    set({ status: "authenticated", session, hasSession: true, tenantSlug, signedOut: false });
  },

  setTenantSlug: (slug) => {
    writeStorage(AUTH_STORAGE_KEYS.tenantSlug, slug);
    set({ tenantSlug: slug });
  },

  clear: (options) => {
    writeStorage(AUTH_STORAGE_KEYS.hasSession, null);
    set({ status: "anonymous", session: null, hasSession: false, signedOut: options?.signedOut ?? false });
  },

  rehydrate: () => {
    writeStorage(LEGACY_REFRESH_TOKEN_KEY, null);
    const hasSession = readStorage(AUTH_STORAGE_KEYS.hasSession) === "1";
    const tenantSlug = readStorage(AUTH_STORAGE_KEYS.tenantSlug);
    set((state) => ({
      hasSession: state.hasSession || hasSession,
      tenantSlug: state.tenantSlug ?? tenantSlug,
      status:
        state.status === "authenticated"
          ? "authenticated"
          : state.hasSession || hasSession
            ? "restoring"
            : "anonymous",
    }));
  },
}));
